import { InMemoryCache } from '@apollo/client';
import { LRWriteMap } from '@frameio/basics/ds/lru';

const APOLLO_ROOT_KEYS = new Set([
  'ROOT_QUERY',
  'ROOT_MUTATION',
  'ROOT_SUBSCRIPTION',
]);

const TRACKED_CAP = 10_000;

export class TrackingCache extends InMemoryCache {
  readonly #tracked = new LRWriteMap<string, number>(TRACKED_CAP);

  constructor(...args: ConstructorParameters<typeof InMemoryCache>) {
    super(...args);
    this.#attachProxy();
  }

  override restore(...args: Parameters<InMemoryCache['restore']>): this {
    const result = super.restore(...args);
    this.#tracked.clear();
    this.#attachProxy();
    return result;
  }

  override reset(...args: Parameters<InMemoryCache['reset']>): Promise<void> {
    return super.reset(...args).then(() => {
      this.#tracked.clear();
      this.#attachProxy();
    });
  }

  *#generate(since?: number, filter?: RegExp): Generator<[string, number]> {
    for (const [id, mtime] of this.#tracked) {
      if (since !== undefined && mtime < since) break;
      // eslint-disable-next-line no-continue
      if (filter && !filter.test(id)) continue;
      yield [id, mtime];
    }
  }

  entitiesChanged = (
    since?: number,
    filter?: RegExp
  ): Generator<[string, number]> => this.#generate(since, filter);

  #attachProxy() {
    if (typeof Proxy !== 'function') return;
    const tracked = this.#tracked;
    // Apollo marks `data` private but there is no public API for
    // intercepting cache writes at this granularity.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entityStore = (this as any).data as {
      data: Record<string, unknown>;
    };
    entityStore.data = new Proxy(entityStore.data, {
      set(target, prop, value, receiver) {
        if (
          typeof prop === 'string' &&
          !APOLLO_ROOT_KEYS.has(prop) &&
          target[prop] !== value
        ) {
          tracked.set(prop, Date.now());
        }
        return Reflect.set(target, prop, value, receiver);
      },
    });
  }
}
